import {
  type ApplicationConfig,
  provideBrowserGlobalErrorListeners,
  provideZonelessChangeDetection,
} from '@angular/core';
import {
  provideHttpClient,
  withFetch,
  withInterceptors,
  withNoXsrfProtection,
} from '@angular/common/http';
import {
  PreloadAllModules,
  provideRouter,
  withComponentInputBinding,
  withInMemoryScrolling,
  withPreloading,
  withRouterConfig,
} from '@angular/router';
import { routes } from './app.routes';
import { provideAuthApi } from './core/auth/provide-auth-api';
import { provideRuntimeConfig } from './core/config/runtime-config';
import { HTTP_INTERCEPTORS_IN_ORDER } from './core/interceptors/http-interceptors';

/**
 * Application providers.
 *
 * `provideZonelessChangeDetection()` IS called, even though Angular 22 is zoneless
 * by DEFAULT and this call is redundant with that default. It is kept as an
 * executable assertion rather than removed: `zone.js` is not a dependency of this
 * project (see `package.json`), and if something ever re-introduced it as a
 * transitive dependency of a future package, this line is what would make the app
 * fail loudly at bootstrap instead of silently falling back to zone-based change
 * detection. A comment saying "zoneless by default" asserts nothing the compiler
 * checks; this call does.
 *
 * HTTP: the interceptor chain lives in `core/interceptors/http-interceptors.ts`, where its
 * order is documented. Angular's built-in XSRF handling is switched off with
 * `withNoXsrfProtection` because the API uses its own cookie and header (`hrm_csrf` and
 * `X-CSRF`, from the runtime config), which `csrfInterceptor` copies. Leaving the built-in
 * one on would add a second, unused `X-XSRF-TOKEN` header.
 *
 * Runtime config (`assets/config.json`) loads in an app initializer, so it is read before the
 * first route and before any interceptor runs. `provideAuthApi` picks the real or in-memory
 * sign-in from it.
 *
 * Each router feature below is here for a reason worth stating:
 *
 *   withComponentInputBinding  route params AND query params arrive as signal
 *                              inputs, so a detail page needs no ActivatedRoute
 *                              subscription and has no unsubscribe to forget.
 *   withInMemoryScrolling      without it, navigating to a new page keeps the old
 *                              scroll position.
 *   withPreloading             PreloadAllModules fetches every lazy route's chunk
 *                              in the background after the initial one finishes.
 *                              HRM is an authenticated, always-online app, so the
 *                              up-front download is affordable. Reconsider it
 *                              (a route-scoped strategy, or none) if the route
 *                              count grows enough to compete with real work on
 *                              slow phones.
 *   onSameUrlNavigation        re-running a resolver on a link to the current URL is
 *                              what a user pressing a nav item they are already on
 *                              expects.
 */
export const appConfig: ApplicationConfig = {
  providers: [
    provideBrowserGlobalErrorListeners(),
    provideZonelessChangeDetection(),
    // withFetch uses the fetch API rather than XHR: smaller, and the only backend
    // that works under SSR should the app ever enable it.
    provideRuntimeConfig(),
    provideAuthApi(),
    provideHttpClient(
      withInterceptors([...HTTP_INTERCEPTORS_IN_ORDER]),
      withFetch(),
      withNoXsrfProtection(),
    ),
    provideRouter(
      routes,
      withComponentInputBinding(),
      withInMemoryScrolling({ scrollPositionRestoration: 'top', anchorScrolling: 'enabled' }),
      withPreloading(PreloadAllModules),
      withRouterConfig({ onSameUrlNavigation: 'reload' }),
    ),
  ],
};
